import * as S from "./styles.ts";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
} from "@measured/dnd";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { v4 } from "uuid";
import { Button } from "@template/components";
import fieldTypes from "@/admin/config/overrides/fieldTypes";
import { CopyPlus } from "lucide-react";
import X from "@template/icons/X.tsx";
import Plus from "@template/icons/Plus.tsx";
import { isEmpty } from "@helpers/isEmpty.ts";

type TItem = {
  title: string;
  content: string;
  key: any;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  background: "red",
  zIndex: 2,
  ...draggableStyle,
});

//fieldTypes[field.type]?.({
//                                         value: item[field.key],
//                                         field,
//                                         onChange: (val: any) => {
//                                             const newItems = JSON.parse(
//                                                 JSON.stringify(items)
//                                             );
//                                             newItems[index][field.key] = val;
//                                             onChange(newItems);
//                                         },
//                                         ...field
//                                     }) || null}

const CustomComponent = ({
  field,
  item,
  items,
  onChange,
  index,
}: {
  field: any;
  item: any;
  items: any[];
  onChange: (val: any) => void;
  index: number;
}) => {
  const Component = fieldTypes[field.type];

  if (!Component) return;

  const { key, ...rest } = field;

  return (
    <Component
      value={item[field.key]}
      field={field}
      onChange={(val: any) => {
        const newItems = JSON.parse(JSON.stringify(items));
        newItems[index][field.key] = val;
        onChange(newItems);
      }}
      {...rest}
    />
  );
};

const Item = ({
  item,
  fields,
  provided,
  onChange,
  index,
  isOpen,
  toggleIsOpen,
  items,
  itemLabel,
}: {
  item: any;
  fields: any;
  provided: DraggableProvided;
  onChange: (any: any) => void;
  index: number;
  isOpen: boolean;
  toggleIsOpen: () => void;
  items: any[];
  itemLabel?: string;
}) => {
  const fieldsArray = Object.keys(fields).map((key) => ({
    key,
    ...fields[key],
  }));

  const onDelete = () => {
    onChange(items.filter((i) => i.key !== item.key));
  };

  const onDuplicate = (e) => {
    e.stopPropagation();
    const newItem = { ...item, key: v4() };
    onChange([...items, newItem]);
  };

  const hasFields = fieldsArray.length > 0;

  return (
    <S.ItemWrapper>
      <S.ItemHeader
        onClick={() => hasFields && toggleIsOpen()}
        className="cursor-pointer hover:bg-primary-50"
        {...provided.dragHandleProps}
      >
        <S.ItemLabel>{itemLabel}</S.ItemLabel>
        <S.RightSection>
          <Button
            color="primary"
            size="sm"
            variant="light"
            onClick={onDuplicate}
          >
            <CopyPlus width={14} />
          </Button>
          <Button
            color="error"
            size="sm"
            variant="light"
            onClick={onDelete}
          >
            <X />
          </Button>
        </S.RightSection>
      </S.ItemHeader>
      <AnimatePresence>
        {isOpen && hasFields && (
          <S.ItemContainerWrapper
            as={motion.div}
            initial="collapsed"
            animate="open"
            exit="collapsed"
            transition={{
              duration: 0.2,
              ease: "easeOut",
            }}
            variants={{
              open: {
                opacity: 1,
                height: "auto",
              },
              collapsed: {
                opacity: 0,
                height: 0,
                overflow: "clip",
              },
            }}
          >
            <S.ItemContainer>
              {fieldsArray.map((field, i) => (
                <CustomComponent
                  key={field.key}
                  field={field}
                  item={item}
                  items={items}
                  onChange={onChange}
                  index={index}
                />
              ))}
            </S.ItemContainer>
          </S.ItemContainerWrapper>
        )}
      </AnimatePresence>
    </S.ItemWrapper>
  );
};

const DragAndDropAccordion = ({
  items,
  fields,
  onChange,
  itemLabel,
}: {
  items: TItem[];
  fields: any[];
  onChange: (val: any) => void;
  itemLabel?: string;
}) => {
  const queryAttr = "data-rfd-draggable-id";
  const [placeholderProps, setPlaceholderProps] = useState<any>({});
  const [openItems, setOpenItems] = useState([]);

  const getDraggedDom = (draggableId: string) => {
    const domQuery = `[${queryAttr}='${draggableId}']`;
    return document.querySelector(domQuery);
  };

  const onAdd = () => {
    const newItem = Object.keys(fields).reduce((acc, key) => {
      acc[key] = fields[key].default;
      return acc;
    }, {});
    newItem["key"] = v4();
    onChange([...items, newItem]);
  };

  const handleDragStart = (event) => {
    const draggedDOM: any = getDraggedDom(event.draggableId);

    if (!draggedDOM) {
      return;
    }

    const { clientHeight, clientWidth } = draggedDOM;
    const sourceIndex = event.source.index;
    const clientY =
      parseFloat(window.getComputedStyle(draggedDOM?.parentNode).paddingTop) +
      [...draggedDOM.parentNode.children]
        .slice(0, sourceIndex)
        .reduce((total, curr) => {
          const style = curr.currentStyle || window.getComputedStyle(curr);
          const marginBottom = parseFloat(style.marginBottom);
          return total + curr.clientHeight + marginBottom;
        }, 0);

    setPlaceholderProps({
      clientHeight,
      clientWidth,
      clientY,
      clientX: parseFloat(
        window.getComputedStyle(draggedDOM.parentNode).paddingLeft
      ),
    });
  };

  const handleDragEnd = (result) => {
    setPlaceholderProps({});

    const { source, destination } = result;
    if (source && destination) {
      const sourceIndex = source.index;
      const destinationIndex = destination.index;
      const sourceUuid = source.droppableId;

      // put item in new position, put item in old position one higher
      const newItems = [...items];
      const [removed] = newItems.splice(sourceIndex, 1);
      newItems.splice(destinationIndex, 0, removed);
      onChange(newItems);
    }
  };

  const handleDragUpdate = (event) => {
    if (!event.destination) {
      return;
    }

    const draggedDOM: any = getDraggedDom(event.draggableId);

    if (!draggedDOM) {
      return;
    }

    const { clientHeight, clientWidth } = draggedDOM;
    const destinationIndex = event.destination.index;
    const sourceIndex = event.source.index;

    const childrenArray = [...draggedDOM.parentNode.children];
    const movedItem = childrenArray[sourceIndex];
    childrenArray.splice(sourceIndex, 1);

    const updatedArray = [
      ...childrenArray.slice(0, destinationIndex),
      movedItem,
      ...childrenArray.slice(destinationIndex + 1),
    ];

    var clientY =
      parseFloat(window.getComputedStyle(draggedDOM.parentNode).paddingTop) +
      updatedArray.slice(0, destinationIndex).reduce((total, curr) => {
        const style = curr.currentStyle || window.getComputedStyle(curr);
        const marginBottom = parseFloat(style.marginBottom);
        return total + curr.clientHeight + marginBottom;
      }, 0);

    setPlaceholderProps({
      clientHeight,
      clientWidth,
      clientY,
      clientX: parseFloat(
        window.getComputedStyle(draggedDOM.parentNode).paddingLeft
      ),
    });
  };

  return (
    <S.ArrayWrapper>
      <S.ArrayContainer $array>
        <DragDropContext
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          onDragUpdate={handleDragUpdate}
        >
          <Droppable droppableId="options-sidebar">
            {(containerProvided, containerSnapshot) => (
              <div
                ref={containerProvided.innerRef}
                {...containerProvided.droppableProps}
              >
                <S.ItemsWrapper>
                  <AnimatePresence>
                    {(items || []).map((item, index) => (
                      <Draggable
                        key={item.key}
                        draggableId={item.key}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <motion.div
                            ref={provided.innerRef}
                            transition={{
                              duration: 0.2,
                              ease: "easeOut",
                            }}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                            {...provided.draggableProps}
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            variants={{
                              open: {
                                opacity: 1,
                                height: "auto",
                              },
                              collapsed: {
                                opacity: 0,
                                height: 0,
                                overflow: "clip",
                              },
                            }}
                          >
                            <Item
                              item={item}
                              fields={fields}
                              provided={provided}
                              onChange={onChange}
                              index={index}
                              isOpen={openItems.includes(item.key)}
                              toggleIsOpen={() => {
                                if (openItems.includes(item.key)) {
                                  setOpenItems(
                                    openItems.filter((i) => i !== item.key)
                                  );
                                } else {
                                  setOpenItems([...openItems, item.key]);
                                }
                              }}
                              items={items}
                              itemLabel={itemLabel}
                            />
                          </motion.div>
                        )}
                      </Draggable>
                    ))}
                  </AnimatePresence>
                  {containerProvided.placeholder}
                  {!isEmpty(placeholderProps) &&
                    containerSnapshot.isDraggingOver && (
                      <S.Placeholder
                        className="border-solid"
                        style={{
                          top: placeholderProps.clientY,
                          left: placeholderProps.clientX,
                          height: placeholderProps.clientHeight - 8,
                          width: placeholderProps.clientWidth,
                        }}
                      />
                    )}
                </S.ItemsWrapper>
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <Button
          onClick={onAdd}
          color="secondary"
          size="sm"
          variant="flat"
        >
          <Plus /> {itemLabel}
        </Button>
      </S.ArrayContainer>
    </S.ArrayWrapper>
  );
};

export default DragAndDropAccordion;
